import { useTranslation } from 'react-i18next';
import { TicketPriority, ticketPriorityLabelKey } from '@nbh/shared';

/**
 * Priority is conveyed three ways at once — shape, icon and text — so it survives a
 * dim plant room, a scratched screen and colour-blind eyes. Colour is the least
 * reliable channel here and is never the only one (SPEC.md §5.1.2 UI note).
 *
 * One glyph per level, not a shape outline and a separate stock icon glued beside
 * it — that combination read as two half-finished marks rather than one. Low/Medium
 * /High share a family (short answering to tall bars): the same reasoning Jira's own
 * accessible priority-icon redesign gives for repeating and scaling one form rather
 * than assigning arbitrary unrelated pictograms — "ascending sequence of urgency" is
 * legible at a glance without memorising what an octagon means. Urgent breaks the
 * family on purpose: "stop and act now" is a different kind of fact from "more work
 * than Medium," not a fourth bar, so it gets the one categorically different mark —
 * a filled hazard diamond with a bolt cut out of it, the plant-room's own vernacular
 * for "this one is not like the others."
 */

type ShapeName = 'chevron-down' | 'bars-2' | 'bars-3' | 'alert-diamond';

interface PriorityStyle {
  shape: ShapeName;
  /** Tailwind classes for the outline and text. */
  tone: string;
}

const STYLES: Record<TicketPriority, PriorityStyle> = {
  [TicketPriority.Urgent]: {
    shape: 'alert-diamond',
    tone: 'text-red-200 bg-red-950/60 ring-2 ring-red-400',
  },
  [TicketPriority.High]: {
    shape: 'bars-3',
    tone: 'text-orange-200 bg-orange-950/60 ring-2 ring-orange-400',
  },
  [TicketPriority.Medium]: {
    shape: 'bars-2',
    tone: 'text-amber-200 bg-amber-950/60 ring-2 ring-amber-400',
  },
  [TicketPriority.Low]: {
    shape: 'chevron-down',
    tone: 'text-emerald-200 bg-emerald-950/60 ring-2 ring-emerald-400',
  },
};

/**
 * The whole signal drawn as one mark, so the shape reads at any size and never
 * depends on a font. Bars are solid fills rather than thin strokes: a silhouette
 * survives glare and a scratched screen better than a 2px outline does, and it is
 * the difference between a badge that looks drawn once, deliberately, and two
 * borrowed glyphs sitting next to each other.
 */
function PriorityGlyph({ shape }: { shape: ShapeName }) {
  const box = 'size-5 shrink-0';

  if (shape === 'chevron-down') {
    return (
      <svg viewBox="0 0 24 24" className={box} aria-hidden="true" focusable="false">
        <path
          d="M5 8l7 8 7-8"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (shape === 'bars-2') {
    return (
      <svg viewBox="0 0 24 24" className={box} aria-hidden="true" focusable="false">
        <rect x="5" y="16" width="6" height="5" rx="1" fill="currentColor" />
        <rect x="13" y="9" width="6" height="12" rx="1" fill="currentColor" />
      </svg>
    );
  }

  if (shape === 'bars-3') {
    return (
      <svg viewBox="0 0 24 24" className={box} aria-hidden="true" focusable="false">
        <rect x="2" y="16" width="5" height="5" rx="1" fill="currentColor" />
        <rect x="9.5" y="11" width="5" height="10" rx="1" fill="currentColor" />
        <rect x="17" y="5" width="5" height="16" rx="1" fill="currentColor" />
      </svg>
    );
  }

  // alert-diamond: a hazard diamond with a bolt punched out of its own fill —
  // one path, two closed subpaths, `evenodd` turns the overlap into a hole rather
  // than needing an <svg:mask> and an id that would collide across a queue full
  // of Urgent rows.
  return (
    <svg viewBox="0 0 24 24" className={box} aria-hidden="true" focusable="false">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2L22 12L12 22L2 12Z M13 6L8.5 13L11.5 13L10.5 18L16 10.5L12.5 10.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

export interface PriorityBadgeProps {
  priority: TicketPriority;
  /** Hide the text label where space is tight, e.g. a dense queue row. */
  compact?: boolean;
}

export function PriorityBadge({ priority, compact = false }: PriorityBadgeProps) {
  const { t } = useTranslation();
  const { shape, tone } = STYLES[priority];
  const label = t(ticketPriorityLabelKey[priority]);

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-semibold ${tone}`}
      data-priority={priority}
      data-shape={shape}
      // Compact badges lose their visible text, so the name still reaches a screen reader.
      aria-label={compact ? label : undefined}
      title={compact ? label : undefined}
    >
      <PriorityGlyph shape={shape} />
      {!compact && <span>{label}</span>}
    </span>
  );
}
